import api from './api';

/**
 * Admin-Service: Profi-Ranglisten + KI-Daten-Sync.
 * Exakt nach dem Vorbild von adminProTournamentsService.
 *
 * Ranglisten-CRUD: /api/admin/pro-ranking* (bestehende Backend-API)
 * Sync (Quellen/Settings/Läufe): /api/admin/pro-ranking-sync/*
 * 4xx-Fehler kommen als Plaintext (getErrorMessage nutzen).
 */

export interface ProRankingAdmin {
  _id?: string | null;
  id?: string | null;
  slug?: string | null;
  name?: string | null;
  shortName?: string | null;
  description?: string | null;
  organization?: string | null;
  image?: string | null;
  valueUnit?: string | null;
  valueLabel?: string | null;
  valueMultiplier?: number | null;
  valueMultiplierHint?: string | null;
  dataAsOf?: string | null;
  entryCount?: number | null;
  active?: boolean | null;
  autoCrawlEnabled?: boolean | null;
  sortOrder?: number | null;
  // Redaktionelle Info-Felder (KI-Sync)
  intro?: string | null;
  overview?: string | null;
  howItWorks?: string | null;
  keyFacts?: { icon?: string | null; label?: string | null; value?: string | null; order?: number | null }[] | null;
  records?: { label?: string | null; value?: string | null; holder?: string | null; year?: number | null; note?: string | null }[] | null;
  pastLeaders?: { year?: number | null; playerName?: string | null; note?: string | null }[] | null;
  thumbnail?: string | null;
  heroImage?: string | null;
  sponsor?: string | null;
  officialWebsite?: string | null;
  updatedAt?: number | null;
  updatedBy?: string | null;
  createdAt?: number | null;
}

export interface ProRankingSyncSource {
  id?: string | null;
  rankingId?: string | null;
  name?: string | null;
  url?: string | null;
  enabled?: boolean | null;
  lastCheckedAt?: number | null;
  lastError?: string | null;
  createdAt?: number | null;
}

export interface ProRankingSyncSettings {
  enabled?: boolean | null;
  intervalMinutes?: number | null;
  claudeModel?: string | null;
  aiBanners?: boolean | null;
  lastRunAt?: string | number | null;
}

export interface ProRankingSyncRunResult {
  rankingId?: string | null;
  slug?: string | null;
  name?: string | null;
  sourcesUsed?: number | null;
  updatedFields?: string[] | null;
  entriesUpdated?: number | null;
  bannerGenerated?: boolean | null;
  error?: string | null;
}

export interface ProRankingSyncRun {
  id?: string | null;
  startedAt?: number | null;
  finishedAt?: number | null;
  status?: 'running' | 'success' | 'error' | string | null;
  triggeredBy?: string | null;
  rankingsProcessed?: number | null;
  results?: ProRankingSyncRunResult[] | null;
  errors?: string[] | null;
}

class AdminProRankingsService {
  // ---------- Ranglisten ----------

  async listRankings(): Promise<ProRankingAdmin[]> {
    const response = await api.get('/api/admin/pro-rankings');
    const data = response.data;
    if (Array.isArray(data)) return data as ProRankingAdmin[];
    return (data?.rankings as ProRankingAdmin[]) ?? [];
  }

  async createRanking(payload: { name: string; organization?: string }): Promise<ProRankingAdmin> {
    const response = await api.post('/api/admin/pro-ranking', {
      name: payload.name,
      organization: payload.organization || 'PDC',
    });
    return response.data as ProRankingAdmin;
  }

  async updateRanking(id: string, payload: Partial<ProRankingAdmin>): Promise<ProRankingAdmin> {
    const response = await api.put(`/api/admin/pro-ranking/${id}`, payload);
    return response.data as ProRankingAdmin;
  }

  async deleteRanking(id: string): Promise<void> {
    await api.delete(`/api/admin/pro-ranking/${id}`);
  }

  // ---------- Sync-Quellen ----------

  async listSources(rankingId?: string): Promise<ProRankingSyncSource[]> {
    const response = await api.get('/api/admin/pro-ranking-sync/sources', {
      params: rankingId ? { rankingId } : undefined,
    });
    return (response.data?.sources as ProRankingSyncSource[]) ?? [];
  }

  async createSource(payload: {
    rankingId: string;
    url: string;
    name?: string;
    enabled?: boolean;
  }): Promise<ProRankingSyncSource> {
    const response = await api.post('/api/admin/pro-ranking-sync/sources', payload);
    return (response.data?.source as ProRankingSyncSource) ?? {};
  }

  async updateSource(id: string, payload: Partial<{
    name: string;
    url: string;
    enabled: boolean;
  }>): Promise<ProRankingSyncSource> {
    const response = await api.put(`/api/admin/pro-ranking-sync/source/${id}`, payload);
    return (response.data?.source as ProRankingSyncSource) ?? {};
  }

  async deleteSource(id: string): Promise<void> {
    await api.delete(`/api/admin/pro-ranking-sync/source/${id}`);
  }

  // ---------- Einstellungen ----------

  async getSettings(): Promise<ProRankingSyncSettings> {
    const response = await api.get('/api/admin/pro-ranking-sync/settings');
    return (response.data?.settings as ProRankingSyncSettings) ?? {};
  }

  async updateSettings(payload: Partial<{
    enabled: boolean;
    intervalMinutes: number;
    claudeModel: string;
    aiBanners: boolean;
  }>): Promise<ProRankingSyncSettings> {
    const response = await api.put('/api/admin/pro-ranking-sync/settings', payload);
    return (response.data?.settings as ProRankingSyncSettings) ?? {};
  }

  // ---------- Läufe ----------

  /** Lauf starten – optional nur für EINE Rangliste (rankingId). 409 wenn schon aktiv. */
  async triggerRun(rankingId?: string): Promise<void> {
    await api.post('/api/admin/pro-ranking-sync/run', rankingId ? { rankingId } : {});
  }

  async listRuns(limit = 20): Promise<{ runs: ProRankingSyncRun[]; total: number }> {
    const response = await api.get('/api/admin/pro-ranking-sync/runs', { params: { limit } });
    return {
      runs: (response.data?.runs as ProRankingSyncRun[]) ?? [],
      total: response.data?.total ?? 0,
    };
  }

  // ---------- Banner ----------

  /**
   * Lädt ein Bild hoch (POST /api/file) und liefert die /api/file/-URL.
   * WICHTIG: Backend erwartet ROHES Base64 (ohne data:-URL-Prefix).
   */
  async uploadBannerImage(file: File): Promise<string> {
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
      reader.readAsDataURL(file);
    });
    const base64 = dataUrl.includes('base64,') ? dataUrl.split('base64,')[1] : dataUrl;
    const response = await api.post('/api/file', { data: base64 });
    const name = response.data?.name;
    if (!name) throw new Error('Upload fehlgeschlagen');
    return `/api/file/${name}`;
  }

  /** Eigenes Banner setzen (ersetzt KI-Banner). */
  async setRankingBanner(id: string, url: string): Promise<ProRankingAdmin> {
    return this.updateRanking(id, { thumbnail: url, heroImage: url });
  }

  /**
   * Banner entfernen. Hinweis: Ist der KI-Sync mit aiBanners aktiv,
   * generiert der nächste Lauf automatisch wieder ein KI-Banner.
   */
  async removeRankingBanner(id: string): Promise<ProRankingAdmin> {
    return this.updateRanking(id, { thumbnail: null, heroImage: null });
  }

  // ---------- Helper ----------

  getErrorMessage(err: unknown, fallback: string): string {
    const anyErr = err as { response?: { data?: unknown } };
    const data = anyErr?.response?.data;
    if (typeof data === 'string' && data.trim().length > 0) return data;
    return fallback;
  }

  buildFileUrl(path?: string | null): string | null {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${api.defaults.baseURL}${path.startsWith('/') ? '' : '/'}${path}`;
  }
}

const adminProRankingsService = new AdminProRankingsService();
export default adminProRankingsService;
